import React from 'react';

const TONES = {
  brand:   { txt: 'text-brand-700',   dot: 'bg-brand-500',   ring: 'ring-gray-200' },
  emerald: { txt: 'text-emerald-600', dot: 'bg-emerald-500', ring: 'ring-gray-200' },
  sky:     { txt: 'text-sky-600',     dot: 'bg-sky-500',     ring: 'ring-gray-200' },
  amber:   { txt: 'text-amber-600',   dot: 'bg-amber-500',   ring: 'ring-gray-200' },
  rose:    { txt: 'text-rose-600',    dot: 'bg-rose-500',    ring: 'ring-rose-200' },
  violet:  { txt: 'text-violet-600',  dot: 'bg-violet-500',  ring: 'ring-gray-200' },
  gray:    { txt: 'text-gray-700',    dot: 'bg-gray-400',    ring: 'ring-gray-200' },
};

/**
 * Stat tile for KPI strips: small uppercase label, large tabular value, and a
 * dotted sub-line. Set `alert` to show a pulsing rose dot in the corner.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.label - Uppercase label.
 * @param {React.ReactNode} props.value - Large headline value.
 * @param {React.ReactNode} [props.sub] - Sub-line caption.
 * @param {'brand'|'emerald'|'sky'|'amber'|'rose'|'violet'|'gray'} [props.tone='brand']
 *   Semantic colour tone for the value and dot.
 * @param {boolean} [props.alert=false] - Show a pulsing alert dot.
 * @param {() => void} [props.onClick] - When provided, the tile renders as a
 *   button that filters/navigates on click.
 * @param {boolean} [props.active=false] - Highlight the tile as the current selection.
 * @returns {JSX.Element}
 */
export default function KpiTile({ label, value, sub, tone = 'brand', alert = false, onClick, active = false }) {
  const t = TONES[tone] || TONES.brand;
  const interactive = typeof onClick === 'function';
  const Comp = interactive ? 'button' : 'div';
  const ringCls = active ? 'ring-2 ring-brand-400' : t.ring;
  const interactiveCls = interactive
    ? 'text-left w-full transition hover:ring-brand-300 hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400'
    : '';
  return (
    <Comp
      {...(interactive ? { type: 'button', onClick, 'aria-pressed': active } : {})}
      className={`bg-white dark:bg-gray-800 rounded-xl ring-1 ${ringCls} dark:ring-gray-700 px-5 py-4 relative ${interactiveCls}`}
    >
      {alert && (
        <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
      )}
      <p className="text-[12.5px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">{label}</p>
      <p className={`text-[34px] font-bold tabular leading-none mt-1.5 ${t.txt}`}>{value}</p>
      {sub != null && (
        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
          {sub}
        </p>
      )}
    </Comp>
  );
}
